import { useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "components/ui/dialog";
import PageHeader from "./-components/PageHeader";
import SelectBox from "../morning/-components/common/SelectBox";
import LoadingPop from "components/popup/LoadingPop";
import { formatDateToString, shortcut } from "hooks/utils/util";
import { ConsoleOrderRequest, useGetConsoleQuickOrder } from "hooks/console/useGetConsoleQuickOrder";
import ConsoleOrderForm, { SearchFormProps } from "./common/ConsoleOrderForm";
import { DataListProps } from "./-components/ConsoleTable";
import Pagination from "components/Pagination";
import { useGetConsoleQuickOrderDetail } from "hooks/console/useGetConsoleQuickOrderDetail";
import Seo from "components/Seo";
// import { ReactComponent as PageBefore } from "assets/morning/icon/pageBefore.svg";
// import { ReactComponent as PageFirst } from "assets/morning/icon/pageFirst.svg";

const initialSearchForm = {
    category: "quick",
    user_id: "",
    limit: "10",
    from_date: new Date(),
    to_date: new Date(),
    serial_number: "",
    order_state: "",
    delivery_type: "",
    car_type: "",
    departure_dong_name: "",
    payment_type: "",
    destination_dong_name: "",
};
// const maxPage = 1;

const ConsoleQuickOrder = () => {
    const [page, setPage] = useState<number>(1);
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchForm, setSearchForm] = useState<SearchFormProps>(initialSearchForm); // 입력 중인 값
    const [submittedForm, setSubmittedForm] = useState<ConsoleOrderRequest | null>(null); // 제출된 상태만 API 요청에 사용
    const [orderDetail, setOrderDetail] = useState<DataListProps | null>(null);
    const viewDetailDialogRef = useRef<HTMLButtonElement | null>(null);
    const location = useLocation();
    const { data: orderData, isLoading } = useGetConsoleQuickOrder(submittedForm);
    const { data: orderDetailData, isLoading: isLoadingOrderDetail } = useGetConsoleQuickOrderDetail({
        category: searchForm.category,
        serial_number: orderDetail?.serial_number,
    });
    const { 퀵화물인터넷접수 } = shortcut();
    // const maxPage = data?.last_page ?? 1;
    // const pageSize = Number(searchForm.limit);
    // const currentGroup = Math.floor((page - 1) / pageSize);
    // const startPage = currentGroup * pageSize + 1;
    // const endPage = Math.min(startPage + pageSize - 1, maxPage);

    // 사이드바 링크 재 클릭시 상태 초기화
    useEffect(() => {
        if (location.state?.reset) {
            // 초기화 로직 수행
            setSearchForm(initialSearchForm);
            setPage(1);
        }
    }, [location.state]);

    useEffect(() => {
        const page = searchParams.get("page") ?? "1";
        if (page) {
            setSearchForm(prev => ({ ...prev, page: Number(page) }));
        }
    }, [searchParams]);

    // 페이징, 개수 필터 셀렉트 수정시 검색 실행
    useEffect(() => {
        searchHandler();
    }, [page, searchForm.limit]);

    // 검색 버튼 클릭시
    const searchHandler = () => {
        setSubmittedForm({
            ...searchForm,
            page,
            from_date: searchForm.from_date ? formatDateToString(searchForm.from_date) : "",
            to_date: searchForm.to_date ? formatDateToString(searchForm.to_date) : "",
        });
    };

    // 검색 폼 초기화
    const resetForm = () => {
        setSearchForm(initialSearchForm);
        setPage(1);
    };

    const handleViewDetail = (row: DataListProps) => {
        // console.log("자세히 보기 클릭:", row);
        setOrderDetail(row);
        viewDetailDialogRef.current?.click();
    };

    return (
        <>
            <Seo type="console" pageTitle="퀵&화물 인터넷접수" />
            <div className="flex-1 flex flex-col overflow-hidden max-h-[100dvh]">
                <PageHeader category="고객서비스" title="퀵&화물 인터넷접수" linkTo={퀵화물인터넷접수} />
                <div className="flex-1 bg-[#F7F6FB] flex overflow-hidden">
                    <div className="px-[40px] py-[28px] w-full overflow-auto">
                        <div className="flex flex-col gap-10 min-w-[1200px]">
                            <ConsoleOrderForm
                                searchForm={searchForm}
                                setSearchForm={setSearchForm}
                                searchHandler={searchHandler}
                                resetForm={resetForm}
                            />
                            <div className="bg-white flex flex-col gap-4 rounded-[20px] py-[24px] px-[40px]">
                                <SelectBox
                                    options={[
                                        { label: "10개", value: "10" },
                                        { label: "20개", value: "20" },
                                    ]}
                                    value={searchForm.limit}
                                    onChange={val => setSearchForm(prev => ({ ...prev, limit: val }))}
                                    height="h-[50px]"
                                    className="w-[160px] !text-[15px] ml-auto"
                                />
                                {isLoading ? (
                                    <LoadingPop />
                                ) : (
                                    <table className="text-[#666] table-fixed w-full">
                                        <colgroup>
                                            <col width="5%" />
                                            <col width="7%" />
                                            <col width="7%" />
                                            <col width="8%" />
                                            <col width="8%" />
                                            <col width="8%" />
                                            <col width="8%" />
                                            <col width="26%" />
                                            <col width="5%" />
                                            <col width="5%" />
                                            <col width="5%" />
                                            <col width="8%" />
                                        </colgroup>

                                        <thead className="bg-[#FBFBFC] h-[90px] border-t-2 border-black border-b w-full">
                                            <tr className="w-full">
                                                <th className="font-[400] border-r border-[#ddd] whitespace-nowrap">접수일</th>
                                                <th className="font-[400] border-r whitespace-nowrap">
                                                    거래처
                                                    <br />
                                                    (접수자)
                                                </th>
                                                <th className="font-[400] border-r border-[#ddd]">접수자부서명</th>
                                                <th className="font-[400] border-r border-[#ddd]">
                                                    <div className="flex flex-col h-[90px]">
                                                        <p className="border-b flex items-center justify-center h-full whitespace-nowrap">
                                                            처리상태
                                                        </p>
                                                        <p className="flex items-center justify-center h-full whitespace-nowrap">
                                                            지불수단
                                                        </p>
                                                    </div>
                                                </th>
                                                <th className="font-[400] border-r border-[#ddd]">
                                                    <div className="flex flex-col h-[90px]">
                                                        <p className="border-b flex items-center justify-center h-full whitespace-nowrap">
                                                            차량
                                                        </p>
                                                        <p className="flex items-center justify-center h-full whitespace-nowrap">구분</p>
                                                    </div>
                                                </th>
                                                <th className="font-[400] border-r border-[#ddd]">
                                                    <div className="flex flex-col h-[90px]">
                                                        <p className="border-b flex items-center justify-center h-full whitespace-nowrap">
                                                            출발지고객명
                                                        </p>
                                                        <p className="flex items-center justify-center h-full whitespace-nowrap">
                                                            도착지고객명
                                                        </p>
                                                    </div>
                                                </th>
                                                <th className="font-[400] border-r border-[#ddd]">
                                                    <div className="flex flex-col h-[90px]">
                                                        <p className="border-b flex items-center justify-center h-full whitespace-nowrap">
                                                            출발지동명
                                                        </p>
                                                        <p className="flex items-center justify-center h-full whitespace-nowrap">
                                                            도착지동명
                                                        </p>
                                                    </div>
                                                </th>
                                                <th className="font-[400] border-r border-[#ddd]">
                                                    <div className="flex flex-col h-[90px]">
                                                        <div className="border-b flex h-full grid grid-cols-[50%_25%_25%]">
                                                            <p className="items-center justify-center flex border-r h-full whitespace-nowrap">
                                                                출발지주소
                                                            </p>
                                                            <p className="items-center justify-center flex border-r h-full whitespace-nowrap">
                                                                출발지부서
                                                            </p>
                                                            <p className="items-center justify-center flex h-full whitespace-nowrap">
                                                                출발지담당
                                                            </p>
                                                        </div>
                                                        <p className=" flex items-center justify-center h-full whitespace-nowrap">
                                                            도착지주소
                                                        </p>
                                                    </div>
                                                </th>
                                                <th className="font-[400] border-r border-[#ddd] whitespace-nowrap">추가금액</th>
                                                <th className="font-[400] border-r border-[#ddd] whitespace-nowrap">기본금액</th>
                                                <th className="font-[400] border-r border-[#ddd] whitespace-nowrap">총금액</th>
                                                <th className="font-[400] whitespace-nowrap">상세보기</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {orderData && orderData?.data_list.length > 0 ? (
                                                <>
                                                    {orderData?.data_list.map(data => {
                                                        return (
                                                            <tr key={data.serial_number} className="border-b border-black">
                                                                <td className="font-[400] w-[80px] border-r border-[#ddd] text-center px-2">
                                                                    <p className="truncate text-[#999999]">
                                                                        {data.order_date.split(" ")[0].replaceAll("-", ".")}
                                                                    </p>
                                                                </td>
                                                                <td className="font-[400] w-[120px] border-r text-center px-2">
                                                                    <p className="truncate">{data.customer_name}</p>
                                                                    {/* <br />
                                                                    ({data.departure_staff}) */}
                                                                </td>
                                                                <td className="font-[400] w-[120px] border-r border-[#ddd] text-center px-2">
                                                                    <p className="truncate">{data.customer_department}</p>
                                                                </td>
                                                                <td className="font-[400] w-[100px] border-r border-[#ddd]">
                                                                    <div className="flex flex-col h-[90px]">
                                                                        <div className="border-b flex items-center justify-center h-full px-2">
                                                                            <p className="truncate">{data.order_state}</p>
                                                                        </div>
                                                                        <div className="flex items-center justify-center h-full px-2">
                                                                            <p className="truncate">{data.payment_type}</p>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td className="font-[400] w-[80px] border-r border-[#ddd]">
                                                                    <div className="flex flex-col h-[90px]">
                                                                        <div className="border-b flex items-center justify-center h-full px-2">
                                                                            <p className="truncate">{data.car_type}</p>
                                                                        </div>
                                                                        <div className="flex items-center justify-center h-full px-2">
                                                                            <p className="truncate">{data.delivery_type}</p>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td className="font-[400] w-[120px] border-r border-[#ddd]">
                                                                    <div className="flex flex-col h-[90px]">
                                                                        <div className="border-b flex items-center justify-center h-full px-2">
                                                                            <p className="truncate">{data.departure_customer}</p>
                                                                        </div>
                                                                        <div className="flex items-center justify-center h-full px-2">
                                                                            <p className="truncate">{data.destination_customer}</p>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td className="font-[400] w-[120px] border-r border-[#ddd]">
                                                                    <div className="flex flex-col h-[90px]">
                                                                        <div className="border-b flex items-center justify-center h-full px-2">
                                                                            <p className="truncate">{data.departure_dong_name}</p>
                                                                        </div>
                                                                        <div className="flex items-center justify-center h-full px-2">
                                                                            <p className="truncate">{data.destination_dong_name}</p>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td className="font-[400] border-r border-[#ddd]">
                                                                    <div className="flex flex-col h-[90px]">
                                                                        <div className="border-b flex h-full grid grid-cols-[50%_25%_25%]">
                                                                            <div className="items-center justify-center flex border-r h-full px-2">
                                                                                <p className="truncate">{data.departure_address}</p>
                                                                            </div>
                                                                            <div className="items-center justify-center flex border-r h-full px-2">
                                                                                <p className="truncate">{data.departure_department}</p>
                                                                            </div>
                                                                            <div className="items-center justify-center flex h-full px-2">
                                                                                <p className="truncate">{data.departure_staff}</p>
                                                                            </div>
                                                                        </div>
                                                                        <div className=" flex items-center justify-center h-full px-2">
                                                                            <p className="truncate">{data.destination_address}</p>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td className="font-[400] border-r border-[#ddd] text-center px-2">
                                                                    <p className="truncate">{data.addition_cost}</p>
                                                                </td>
                                                                <td className="font-[400] border-r border-[#ddd] text-center px-2">
                                                                    <p className="truncate">{data.basic_cost}</p>
                                                                </td>
                                                                <td className="font-[400] border-r border-[#ddd] text-center px-2">
                                                                    <p className="truncate">{data.total_cost}</p>
                                                                </td>
                                                                <td className="font-[400] text-center px-2">
                                                                    <button
                                                                        onClick={() => handleViewDetail(data)}
                                                                        className="bg-[#192030] text-white text-[14px] rounded-[5px] px-[12px] py-[7.5px] text-nowrap"
                                                                    >
                                                                        상세보기
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </>
                                            ) : (
                                                <tr>
                                                    <td colSpan={12} className="text-center py-10">
                                                        내용이 없습니다.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                )}
                                <Pagination
                                    current={page}
                                    total={orderData?.total_count || 1}
                                    pageSize={Number(searchForm.limit)}
                                    onChange={newPage => {
                                        searchParams.set("page", String(newPage));
                                        setSearchParams(searchParams);
                                        // setSearchForm(prev => ({ ...prev, page: newPage }))
                                    }}
                                />

                                {/* <div className="flex justify-center items-center gap-2 mt-6">
                                    <button onClick={() => handleSetPage(1)}>
                                        <PageFirst />
                                    </button>
                                    <button onClick={() => handleSetPage(Math.max(1, page - 1))}>
                                        <PageBefore className="mr-7" />
                                    </button>
                                    {Array.from({ length: endPage - startPage + 1 }).map((_, i) => {
                                        const pageNum = startPage + i;
                                        return (
                                            <button
                                                key={pageNum}
                                                className={`px-3 py-1 rounded ${pageNum === page ? "bg-[#1B1C1E] text-white" : "text-black"}`}
                                                onClick={() => handleSetPage(pageNum)}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                    <button onClick={() => handleSetPage(Math.min(maxPage, page + 1))}>
                                        <PageBefore className="rotate-180 ml-7" />
                                    </button>
                                    <button onClick={() => handleSetPage(maxPage)}>
                                        <PageFirst className="rotate-180" />
                                    </button>
                                </div> */}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Dialog onOpenChange={open => !open && setOrderDetail(null)}>
                <DialogTrigger ref={viewDetailDialogRef} className="absolute" />
                <DialogContent className="bg-[#F7F6FB] p-0 gap-0 w-full max-w-[1000px]">
                    <DialogHeader className="border-b border-[#dddddd] p-[32px_40px_20px]">
                        <DialogTitle className="text-[24px] !text-left">배송상세조회 - 주문번호({orderDetail?.serial_number})</DialogTitle>
                        <DialogDescription hidden />
                    </DialogHeader>
                    <div className="py-[20px] flex flex-col gap-5 items-center overflow-x-auto">
                        <div className="px-[40px] max-h-[70dvh] overflow-auto w-full">
                            <div className="bg-white p-5 w-full">
                                {isLoadingOrderDetail ? (
                                    <LoadingPop />
                                ) : (
                                    <>
                                        {
                                            orderDetailData && (
                                                <figure className="w-full overflow-x-auto">
                                                    <table className="w-full border-t border-b min-w-[750px]">
                                                        <tbody>
                                                            <tr className="border-b">
                                                                <th
                                                                    rowSpan={2}
                                                                    className="bg-[#f8f8f8] font-normal text-[15px] w-[15%] border-r"
                                                                >
                                                                    접수자정보
                                                                </th>
                                                                <th className="font-normal text-[15px] w-[20%] border-r py-2">상호</th>
                                                                <td className="border-r text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.customer_info[0]}
                                                                </td>
                                                                <th className="font-normal text-[15px] w-[20%] border-r py-2">연락처</th>
                                                                <td className="text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.customer_info[1]}
                                                                </td>
                                                            </tr>
                                                            <tr className="border-b">
                                                                <th className="font-normal text-[15px] w-[20%] border-r py-2">부서명</th>
                                                                <td className="border-r text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.customer_info[2]}
                                                                </td>
                                                                <th className="font-normal text-[15px] w-[20%] border-r py-2">담당</th>
                                                                <td className="text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.customer_info[3]}
                                                                </td>
                                                            </tr>

                                                            <tr className="border-b">
                                                                <th
                                                                    rowSpan={2}
                                                                    className="bg-[#f8f8f8] font-normal text-[15px] w-[15%] border-r"
                                                                >
                                                                    기사정보
                                                                </th>
                                                                <th className="font-normal text-[15px] w-[20%] border-r py-2">
                                                                    기사고유번호
                                                                </th>
                                                                <td className="border-r text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.rider_info[0]}
                                                                </td>
                                                                <th className="font-normal text-[15px] w-[20%] border-r py-2">기사이름</th>
                                                                <td className="text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.rider_info[1]}
                                                                </td>
                                                            </tr>
                                                            <tr className="border-b">
                                                                <th className="font-normal text-[15px] w-[20%] border-r py-2">
                                                                    기사연락처
                                                                </th>
                                                                <td colSpan={3} className="text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.rider_info[2]}
                                                                </td>
                                                            </tr>

                                                            <tr className="border-b">
                                                                <th
                                                                    rowSpan={3}
                                                                    className="bg-[#f8f8f8] font-normal text-[15px] w-[15%] border-r"
                                                                >
                                                                    주문정보
                                                                </th>
                                                                <th className="font-normal text-[15px] w-[20%] border-r py-2">주문번호</th>
                                                                <td className="border-r text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.order_info[0]}
                                                                </td>
                                                                <th className="font-normal text-[15px] w-[20%] border-r py-2">접수시간</th>
                                                                <td className="text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.order_info[1]}
                                                                </td>
                                                            </tr>
                                                            <tr className="border-b">
                                                                <th className="font-normal text-[15px] w-[20%] border-r py-2">배차시간</th>
                                                                <td className="border-r text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.order_info[2]}
                                                                </td>
                                                                <th className="font-normal text-[15px] w-[20%] border-r py-2">픽업시간</th>
                                                                <td className="text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.order_info[3]}
                                                                </td>
                                                            </tr>
                                                            <tr className="border-b">
                                                                <th className="font-normal text-[15px] w-[20%] border-r py-2">배송사유</th>
                                                                <td colSpan={3} className="text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.order_info[4]}
                                                                </td>
                                                            </tr>

                                                            <tr className="border-b">
                                                                <th
                                                                    rowSpan={5}
                                                                    className="bg-[#f8f8f8] font-normal text-[15px] w-[15%] border-r"
                                                                >
                                                                    배송지정보
                                                                </th>
                                                                <th className="font-normal text-[15px] w-[20%] border-r py-2">
                                                                    출발지동명
                                                                </th>
                                                                <td className="border-r text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.delivery_info[0]}
                                                                </td>
                                                                <th className="font-normal text-[15px] w-[20%] border-r py-2">
                                                                    출발지연락처
                                                                </th>
                                                                <td className="text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.delivery_info[1]}
                                                                </td>
                                                            </tr>
                                                            <tr className="border-b">
                                                                <th className="font-normal text-[15px] w-[20%] border-r py-2">
                                                                    출발지상호(이름)
                                                                </th>
                                                                <td colSpan={3} className="text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.delivery_info[2]}
                                                                </td>
                                                            </tr>
                                                            <tr className="border-b">
                                                                <th className="font-normal text-[15px] w-[20%] border-r py-2">
                                                                    도착지동명
                                                                </th>
                                                                <td className="border-r text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.delivery_info[3]}
                                                                </td>
                                                                <th className="font-normal text-[15px] w-[20%] border-r py-2">
                                                                    도착지연락처
                                                                </th>
                                                                <td className="text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.delivery_info[4]}
                                                                </td>
                                                            </tr>
                                                            <tr className="border-b">
                                                                <th className="font-normal text-[15px] w-[20%] border-r py-2">
                                                                    도착지상호(이름)
                                                                </th>
                                                                <td colSpan={3} className="text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.delivery_info[5]}
                                                                </td>
                                                            </tr>
                                                            <tr className="border-b">
                                                                <th className="font-normal text-[15px] w-[20%] border-r py-2">전달내용</th>
                                                                <td colSpan={3} className="text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.delivery_info[6]}
                                                                </td>
                                                            </tr>

                                                            <tr className="border-b">
                                                                <th
                                                                    rowSpan={4}
                                                                    className="bg-[#f8f8f8] font-normal text-[15px] w-[15%] border-r"
                                                                >
                                                                    배송/요금정보
                                                                </th>
                                                                <th className="font-normal text-[15px] w-[20%] border-r py-2">배송수단</th>
                                                                <td className="border-r text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.cost_info[0]}
                                                                </td>
                                                                <th className="font-normal text-[15px] w-[20%] border-r py-2">지불수단</th>
                                                                <td className="text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.cost_info[1]}
                                                                </td>
                                                            </tr>
                                                            <tr className="border-b">
                                                                <th className="font-normal text-[15px] w-[20%] border-r">배송상태</th>
                                                                <td className="border-r text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.cost_info[2]}
                                                                </td>
                                                                <th className="font-normal text-[15px] w-[20%] border-r py-2">지급금액</th>
                                                                <td className="text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.cost_info[3]}
                                                                </td>
                                                            </tr>
                                                            <tr className="border-b">
                                                                <th className="font-normal text-[15px] w-[20%] border-r py-2">기본요금</th>
                                                                <td className="border-r text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.cost_info[4]}
                                                                </td>
                                                                <th className="font-normal text-[15px] w-[20%] border-r py-2">추가요금</th>
                                                                <td className="text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.cost_info[5]}
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <th className="font-normal text-[15px] w-[20%] border-r py-2">할인요금</th>
                                                                <td className="border-r text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.cost_info[6]}
                                                                </td>
                                                                <th className="font-normal text-[15px] w-[20%] border-r py-2">탁송요금</th>
                                                                <td className="text-[#666666] text-[15px] px-2 py-2">
                                                                    {orderDetailData.cost_info[7]}
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </figure>
                                            )
                                            // <ConsoleHrozontalHeadTable thead={thead} tbody={orderDetail} />
                                        }
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <Toaster position="bottom-center" reverseOrder={false} />
        </>
    );
};

export default ConsoleQuickOrder;
